import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Stack, Button, CircularProgress, Container, Card, CardContent, Grid, Grid2, Modal, Alert, Dialog, DialogContent, DialogContentText, DialogActions, DialogTitle, Link, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { DaySelectionButton } from './DaySelectionButton';
import { TimeSlotButton } from './TimeSlotButton';
import {
  BackendTimeSlot,
  DAYS_MAP,
  DAYS_REVERSE_MAP,
  EnrollmentStatus,
  TIME_SLOTS,
  TIME_SLOTS_REVERSE_MAP,
  UIDay,
  UITimeSlot,
  UserStatus
} from '../../../../core/constant/constant';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { openMailBox } from '../../../../utils/openMailBox';
import { useCoachDemographicsQuery } from '../../../../core/queries/useCoachDemographicsQuery';
import {
  selectCoachGenderDropdownOptions,
  selectCoachEthnicityDropdownOptions,
  selectCoachSexualOrientationDropdownOptions,
} from '../../../../core/store/slices/coachDemographicSlice';

interface ScheduleStepProps {
  isSaving?: boolean;
}

const DEFAULT_COACH_GENDER = '5';
const DEFAULT_COACH_ETHNICITY = '6';
const DEFAULT_COACH_ORIENTATION = '5';

const ScheduleStep: React.FC<ScheduleStepProps> = ({ isSaving=false }) => {
  const availability = useSelector((state: RootState) => state.availability);
  const preferenceInformation = useSelector((state: RootState) => state.preferenceInformation);
  useCoachDemographicsQuery();
  const genderOptions = useSelector(selectCoachGenderDropdownOptions);
  const ethnicityOptions = useSelector(selectCoachEthnicityDropdownOptions);
  const orientationOptions = useSelector(selectCoachSexualOrientationDropdownOptions);
  const { control, formState: { errors }, watch, reset, setValue,  } = useFormContext();
   
  const shiftTimeValue = watch('shiftTime') as BackendTimeSlot | undefined;
  const shiftDaysValues = watch('shiftDays') || [];

  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription);
  const isMemberSubscriptionActive = (Boolean(memberSubscription) && memberSubscription?.status === EnrollmentStatus.MEMBERSHIP_ACTIVE);
  const isActiveCoaching = [UserStatus.ACTIVE_COACHING].includes(basicInfo?.currentStatus as UserStatus);
  const shiftTime = shiftTimeValue && shiftTimeValue in TIME_SLOTS ? TIME_SLOTS[shiftTimeValue as keyof typeof TIME_SLOTS] : '';

  const shiftDays = shiftDaysValues
    .map((dayValue: string) => {
      return dayValue in DAYS_MAP ?
        DAYS_MAP[dayValue as keyof typeof DAYS_MAP] as UIDay :
        undefined;
    })
    .filter((day: any): day is UIDay => day !== undefined);

  useEffect(() => {
    if (!availability) return;

    let isInitialRender = true;

    if (isInitialRender) {
      reset(
        {
          shiftTime: availability.shiftTime || '',
          shiftDays: availability.shiftDays || [],
          preferredCoachGenders: preferenceInformation?.preferredCoachGenders || DEFAULT_COACH_GENDER,
          preferredCoachEthnicities: Array.isArray(preferenceInformation?.preferredCoachEthnicities)
            ? (preferenceInformation.preferredCoachEthnicities[0] || DEFAULT_COACH_ETHNICITY)
            : (preferenceInformation?.preferredCoachEthnicities || DEFAULT_COACH_ETHNICITY),
          preferredCoachSexualOrientations: preferenceInformation?.preferredCoachSexualOrientations || DEFAULT_COACH_ORIENTATION,
        },
        { keepDefaultValues: true }
      );
      isInitialRender = false;
    }
  }, [availability?.shiftTime, availability?.shiftDays, preferenceInformation, reset]);

  const handleTimeSlotChange = (displayTimeSlot: UITimeSlot) => {
    const backendValue = TIME_SLOTS_REVERSE_MAP[displayTimeSlot as keyof typeof TIME_SLOTS_REVERSE_MAP];
    setValue('shiftTime', backendValue);
  };

  const handleDayToggle = (displayDay: UIDay) => {
    const backendDayValue = DAYS_REVERSE_MAP[displayDay as keyof typeof DAYS_REVERSE_MAP];

    const updatedDays = shiftDaysValues.includes(backendDayValue)
      ? shiftDaysValues.filter((d: string) => d !== backendDayValue)
      : [...shiftDaysValues, backendDayValue];
    setValue('shiftDays', updatedDays);
  };


  return (
    <Container>
      {/* <Card>
        <CardContent> */}
          <Stack spacing={{ xs: 2, sm: 4 }}>
            <Box>
              <Typography variant='subtitle1' fontWeight='medium' sx={{ mb: { xs: 1, sm: 2 } }}>
                Preferred Session Time
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Temporarily hidden — no coaches available for these slots
                <TimeSlotButton
                  timeSlot="9:30-11:30 AM ET"
                  label="Morning"
                  currentTimeSlot={shiftTime}
                  setTimeSlot={handleTimeSlotChange}
                  disabled={isSaving}
                />
                <TimeSlotButton
                  timeSlot="12-2 PM ET"
                  label="Midday"
                  currentTimeSlot={shiftTime}
                  setTimeSlot={handleTimeSlotChange}
                  disabled={isSaving}
                />
                <TimeSlotButton
                  timeSlot="2:30-4:30 PM ET"
                  label="Afternoon"
                  currentTimeSlot={shiftTime}
                  setTimeSlot={handleTimeSlotChange}
                  disabled={isSaving}
                />
                */}
                <TimeSlotButton
                  timeSlot="5-7 PM ET"
                  label="Early Evening"
                  currentTimeSlot={shiftTime}
                  setTimeSlot={handleTimeSlotChange}
                  disabled={isSaving}
                />
                <TimeSlotButton
                  timeSlot="7-9 PM ET"
                  label="Late Evening"
                  currentTimeSlot={shiftTime}
                  setTimeSlot={handleTimeSlotChange}
                  disabled={isSaving}
                />
              </Box>
            </Box>

            {/* Preferred Days */}
            <Box>
              <Typography variant='subtitle1' fontWeight='medium' sx={{ mb: { xs: 1, sm: 2 } }}>
                Preferred Days
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {/* Full week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] – weekend hidden temporarily */}
                {(['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const).map((day) => (
                  <DaySelectionButton
                    key={day}
                    day={day}
                    selectedDays={shiftDays}
                    toggleDay={() => handleDayToggle(day)}
                    disabled={isSaving}
                  />
                ))}
              </Box>
              {errors.shiftDays && (
                <Typography color='error' variant='caption' sx={{ mt: 1, display: 'block' }}>
                  {errors.shiftDays.message?.toString()}
                </Typography>
              )}
            </Box>

            {/* Coach Preferences */}
            <Box>
              <Typography variant='subtitle1' fontWeight='medium' sx={{ mb: { xs: 1, sm: 2 } }}>
                Partner Preferences
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Controller
                    name='preferredCoachSexualOrientations'
                    control={control}
                    defaultValue={DEFAULT_COACH_ORIENTATION}
                    render={({ field }) => (
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel>Preferred Partner Sexual Orientation</InputLabel>
                        <Select
                          {...field}
                          value={field.value || DEFAULT_COACH_ORIENTATION}
                          label='Preferred Partner Sexual Orientation'
                          disabled={isSaving}
                        >
                          {orientationOptions.map((opt: { value: string; label: string }) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Controller
                    name='preferredCoachGenders'
                    control={control}
                    defaultValue={DEFAULT_COACH_GENDER}
                    render={({ field }) => (
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel>Preferred Partner Gender</InputLabel>
                        <Select
                          {...field}
                          value={field.value || DEFAULT_COACH_GENDER}
                          label='Preferred Partner Gender'
                          disabled={isSaving}
                        >
                          {genderOptions.map((opt: { value: string; label: string }) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
                  <Controller
                    name='preferredCoachEthnicities'
                    control={control}
                    defaultValue={DEFAULT_COACH_ETHNICITY}
                    render={({ field }) => (
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel>Preferred Partner Race</InputLabel>
                        <Select
                          {...field}
                          value={field.value || DEFAULT_COACH_ETHNICITY}
                          label='Preferred Partner Race'
                          disabled={isSaving}
                        >
                          {ethnicityOptions.map((opt: { value: string; label: string }) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={isSaving || isDisabled}
              color={'primary'}
              sx={{
                minWidth: 180,
                position: 'relative',
                gap: 1,
                fontWeight: 'bold',
                order: { xs: 2, sm: 2 },
                flexGrow: { xs: 1, sm: 0 }
              }}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                  <Typography variant='button' component='span'>
                    Saving...
                  </Typography>
                </>
              ) : (
                <>

                  Submit
                </>
              )}
            </Button> */}
          </Stack>
        {/* </CardContent>
      </Card> */}
      {/* {isActiveCoaching && isMemberSubscriptionActive && (
        <Dialog
          open={true}  
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"You are already enrolled in a coaching program."}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              You are already enrolled in our coaching program. If you are facing any issues, please contact our support team at <Link style={{ cursor: 'pointer' }} color='info' onClick={() => openMailBox()} >support@mindright.io</Link> .
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )} */}
    </Container>
  );
};

export default React.memo(ScheduleStep);